import { useCallback, useMemo, useState } from 'react';
import type { Gene } from '@/types/ontologyGraph';

export type GeneSelectionModalProps = {
  diseaseId: string;
  genes: Gene[];
  onAdd: (diseaseId: string, geneIds: string[]) => void;
  onClose: () => void;
};

export function GeneSelectionModal({ diseaseId, genes, onAdd, onClose }: GeneSelectionModalProps) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const filtered = useMemo(() => {
    if (!search.trim()) return genes;
    const q = search.toLowerCase();
    return genes.filter(g => g.symbol.toLowerCase().includes(q) || g.fullName?.toLowerCase().includes(q));
  }, [genes, search]);

  const toggle = useCallback((id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelected(prev => {
      if (prev.size === filtered.length) return new Set();
      return new Set(filtered.map(g => g.id));
    });
  }, [filtered]);

  const handleAdd = useCallback(() => {
    onAdd(diseaseId, Array.from(selected));
    onClose();
  }, [diseaseId, selected, onAdd, onClose]);

  if (genes.length === 0) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="gene-selection-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.4)',
      }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          width: 'min(420px, 90vw)',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        <h2
          id="gene-selection-title"
          style={{ margin: 0, padding: 16, fontSize: 18, borderBottom: '1px solid #eee' }}
        >
          Select genes to add to graph
        </h2>
        <div style={{ padding: 12 }}>
          <input
            type="search"
            placeholder="Search genes..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ccc',
              borderRadius: 8,
              fontSize: 14,
              boxSizing: 'border-box',
            }}
          />
        </div>
        <button
          type="button"
          onClick={selectAll}
          style={{
            margin: '0 12px 8px',
            padding: '6px 12px',
            fontSize: 13,
            border: '1px solid #4a90d9',
            background: '#fff',
            color: '#4a90d9',
            borderRadius: 6,
            cursor: 'pointer',
            alignSelf: 'flex-start',
          }}
        >
          {selected.size === filtered.length ? 'Deselect all' : 'Select all'}
        </button>
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: '0 12px 12px',
            overflow: 'auto',
            flex: 1,
            minHeight: 200,
          }}
        >
          {filtered.map(g => (
            <li
              key={g.id}
              style={{
                borderRadius: 6,
                background: selected.has(g.id) ? '#e6f0ff' : 'transparent',
              }}
            >
              <label
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 10px',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <input
                  type="checkbox"
                  checked={selected.has(g.id)}
                  onChange={() => toggle(g.id)}
                  style={{ margin: 0, flexShrink: 0 }}
                />
                <span style={{ fontWeight: 500 }}>{g.symbol}</span>
                {g.fullName && <span style={{ fontSize: 12, color: '#666' }}>{g.fullName}</span>}
              </label>
            </li>
          ))}
        </ul>
        <div
          style={{
            padding: 12,
            borderTop: '1px solid #eee',
            display: 'flex',
            gap: 8,
            justifyContent: 'flex-end',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 16px',
              border: '1px solid #ccc',
              borderRadius: 8,
              background: '#fff',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAdd}
            disabled={selected.size === 0}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: 8,
              background: selected.size ? '#4a90d9' : '#ccc',
              color: '#fff',
              cursor: selected.size ? 'pointer' : 'not-allowed',
            }}
          >
            Add to graph ({selected.size})
          </button>
        </div>
      </div>
    </div>
  );
}
